import axios from 'axios';

// Create axios instance WITHOUT auth header for public endpoints
const publicAxios = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    timeout: 30000,
});

/**
 * Validate a workflow access token
 * @param {string} token - The workflow access token
 * @returns {Promise<Object>} Validation result with document and recipient info
 */
export const validateDocumentToken = async (token) => {
    try {
        const response = await publicAxios.post('/document/public/validate-token/', { token });
        if (response.status === 200) {
            return response.data;
        }
    } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 410) {
            return {
                valid: false,
                expired: err.response?.status === 410,
                error: err.response?.data?.error || 'Token is invalid or expired'
            };
        }
        throw err;
    }
};

/**
 * Get public document details using token
 * @param {string} token - The workflow access token
 * @returns {Promise<Object>} Document data with recipient and fields info
 */
export const getPublicDocument = async (token) => {
    try {
        const response = await publicAxios.get(`/document/public/document/${token}/`);
        if (response.status === 200) {
            return response.data;
        }
    } catch (err) {
        if (err.response?.status === 401 || err.response?.status === 410) {
            return {
                valid: false,
                expired: err.response?.status === 410,
                error: err.response?.data?.error || 'Access denied'
            };
        }
        throw err;
    }
};

/**
 * Submit signature for public document signing
 * @param {string} token - The workflow access token
 * @param {Object} signatureData - Signature data including signatures array
 * @returns {Promise<Object>} Result of signing operation
 */
export const submitPublicSignature = async (token, signatureData) => {
    try {
        const response = await publicAxios.post(`/document/public/sign/${token}/`, signatureData);
        if (response.status === 200) {
            return response.data;
        }
    } catch (err) {
        if (err.response?.data?.error) {
            return {
                success: false,
                error: err.response.data.error
            };
        }
        throw err;
    }
};

/**
 * Submit approval for public document
 * @param {string} token - The workflow access token
 * @param {Object} data - Approval data with approved flag and optional comments
 * @returns {Promise<Object>} Result of approval operation
 */
export const submitPublicApproval = async (token, data) => {
    try {
        const response = await publicAxios.post(`/document/public/approve/${token}/`, data);
        if (response.status === 200) {
            return response.data;
        }
    } catch (err) {
        if (err.response?.data?.error) {
            return {
                success: false,
                error: err.response.data.error
            };
        }
        throw err;
    }
};

/**
 * Submit rejection for public document
 * @param {string} token - The workflow access token
 * @param {Object} data - Rejection data with reason and optional comments
 * @returns {Promise<Object>} Result of rejection operation
 */
export const submitPublicRejection = async (token, data) => {
    try {
        const response = await publicAxios.post(`/document/public/reject/${token}/`, data);
        if (response.status === 200) {
            return response.data;
        }
    } catch (err) {
        if (err.response?.data?.error) {
            return {
                success: false,
                error: err.response.data.error
            };
        }
        throw err;
    }
};

/**
 * Mark document as viewed (for VIEWER/CC roles)
 * @param {string} token - The workflow access token
 * @returns {Promise<Object>} Result of view tracking
 */
export const markDocumentViewed = async (token) => {
    try {
        const response = await publicAxios.post(`/document/public/viewed/${token}/`);
        if (response.status === 200) {
            return response.data;
        }
    } catch (err) {
        // Silent fail for view tracking - not critical
        console.warn('Failed to mark document as viewed:', err);
        return { success: false };
    }
};

const publicActions = {
    validateDocumentToken,
    getPublicDocument,
    submitPublicSignature,
    submitPublicApproval,
    submitPublicRejection,
    markDocumentViewed,
};

export default publicActions;
